import React, { createContext, useContext, useEffect, useState } from "react";
import { Button, Typography, VStack } from "components/ui";
import PopupModal from "components/general/PopupModal";
import { useRouter, useGlobalSearchParams, Link } from "expo-router";
import { useToken } from "@gluestack-style/react";

type PopupMessage = {
	id: string;
	title: string;
	content: React.ReactNode;
	actions?: React.ReactNode;
};

type PopupMessageContextType = {
	showMessage: (message: PopupMessage) => void;
	hideMessage: () => void;
	currentMessage: PopupMessage | null;
};

const PopupMessageContext = createContext<PopupMessageContextType | undefined>(undefined);

export const usePopupMessage = () => {
	const context = useContext(PopupMessageContext);
	if (context === undefined) {
		throw new Error("usePopupMessage must be used within a PopupMessageProvider");
	}
	return context;
};

export const PopupMessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [currentMessage, setCurrentMessage] = useState<PopupMessage | null>(null);
	const params = useGlobalSearchParams();
	const router = useRouter();
	const resolvedPrimaryLight = useToken("colors", "primarylight");
	const POPUP_MESSAGES: Record<string, PopupMessage> = {
		arrowhygiene: {
			id: "arrowhygiene",
			title: "Arrow Hygiene is now part of Proquip powered by Abco! 🎉",
			content: (
				<VStack space="md">
					<Typography.H4>Great news — Arrow Hygiene has joined the Proquip team, powered by Abco!</Typography.H4>
					<Typography>
						You've been redirected to{" "}
						<Link href="https://abcopro.co.nz">
							<span style={{ color: resolvedPrimaryLight, fontWeight: "bold" }}>abcopro.co.nz</span>
						</Link>{" "}
						— our new home.
					</Typography>
					<Typography>To continue shopping, please login, you will need to reset your password.</Typography>
				</VStack>
			),
			// actions: (
			// 	<Button
			// 		variant="primary"
			// 		onPress={() => {
			// 			// Add your password reset logic here
			// 			console.log("Reset password clicked");
			// 		}}
			// 		sx={{ marginTop: 20 }}
			// 	>
			// 		<Typography color="white" weight="bold">
			// 			Reset Password
			// 		</Typography>
			// 	</Button>
			// ),
		},
	};
	useEffect(() => {
		// Check for msg parameter in URL
		const msgParam = params.msg as string;
		if (msgParam && POPUP_MESSAGES[msgParam]) {
			setCurrentMessage(POPUP_MESSAGES[msgParam]);

			// Clean up URL parameter after showing message
			// This prevents the popup from showing again on refresh
			if (typeof window !== "undefined" && window.location) {
				const currentUrl = window.location.href;
				const urlWithoutMsg = currentUrl.replace(/[?&]msg=[^&]*/, "").replace(/^&/, "?");
				if (urlWithoutMsg !== currentUrl) {
					window.history.replaceState({}, "", urlWithoutMsg);
				}
			}
		}
	}, [params.msg, router]);

	const showMessage = (message: PopupMessage) => {
		setCurrentMessage(message);
	};

	const hideMessage = () => {
		setCurrentMessage(null);
	};

	const handleCloseModal = () => {
		hideMessage();
	};

	return (
		<PopupMessageContext.Provider value={{ showMessage, hideMessage, currentMessage }}>
			{children}
			{currentMessage && (
				<PopupModal maxWidth={900} animationIn={"fadeIn"} animationOut={"fadeOut"} isVisible={true} title={currentMessage.title} onBackdropPress={handleCloseModal} viewHeight="auto">
					<VStack space="lg">
						{currentMessage.content}
						{currentMessage.actions}
						<Typography color="$textLight600" sx={{ marginTop: 16, fontSize: 14 }}>
							If you have any questions, feel free to contact our support team.
						</Typography>
					</VStack>
				</PopupModal>
			)}
		</PopupMessageContext.Provider>
	);
};
